import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { useBreakpoint, useRouter } from '@main/core-ui';
import { sp } from '@main/theme';

import { sidebarMessages } from './messages';

export const useRouteSkipLink = (
  renderOnChange: any[] = [],
): ((hash: string) => void) => {
  const theme = useTheme();
  const { location, queryParams } = useRouter();
  const { formatMessage } = useIntl();
  const { isBeforeTablet, isAtLeastTablet } = useBreakpoint();

  const handleSkipLink = (headerId: string): void => {
    const button = document.querySelector(
      `#${headerId}-side-menu-list-item button`,
    ) as HTMLButtonElement;

    if (isBeforeTablet) {
      // Toggle the mobile sidebar
      document.getElementById('sidebar-mobile-toggle')?.click();

      // Re-focus the side menu item
      button?.focus();
    } else if (isAtLeastTablet) {
      // Re-focus the side menu item
      button?.focus();
    }
  };

  const renderSkipLinks = (hash: string): void => {
    const headerId = hash.split('#')[1];
    const header = document.getElementById(headerId);
    if (!header) {
      return;
    }

    // Relatively position the header so skip link can be absolutely positioned
    header.style.position = 'relative';
    header.setAttribute('tabindex', '-1');

    const routeSkipLinkHeight = 15;

    // Create skip link element
    const routeSkipLink = document.createElement('a');

    // Set skip link styles and content
    routeSkipLink.style.position = 'absolute';
    routeSkipLink.style.fontSize = sp(2);
    routeSkipLink.style.fontFamily = 'Arial, serif, sans-serif';
    routeSkipLink.style.left = '-18px';
    routeSkipLink.style.padding = '0 2px 2px';
    routeSkipLink.style.top = `${
      header.getBoundingClientRect().height / 2 - routeSkipLinkHeight / 2
    }px`;
    routeSkipLink.style.height = `${routeSkipLinkHeight}px`;
    routeSkipLink.style.display = 'flex';
    routeSkipLink.style.justifyContent = 'center';
    routeSkipLink.style.alignItems = 'center';
    routeSkipLink.style.borderRadius = sp(0.5);
    routeSkipLink.style.color = theme.colors.lightPrimary;
    routeSkipLink.style.boxShadow = `${theme.colors.lightPrimary} 0 0 1px 3px,
                                       ${theme.colors.lightPrimary} 0 0 0 1px,
                                       ${theme.colors.lightPrimary} 0 2px 6px 0,
                                       ${theme.colors.lightPrimary} 0 1px 3.5px 0`;
    routeSkipLink.innerHTML = '&laquo';

    // Set skip link attributes
    routeSkipLink.setAttribute('id', `${headerId}-route-skip-link`);
    routeSkipLink.setAttribute('href', `#${headerId}`);
    routeSkipLink.setAttribute(
      'aria-label',
      formatMessage(sidebarMessages.routeSkipLinkText, {
        title: header.innerText,
      }),
    );

    // Set skip link event listeners
    routeSkipLink.addEventListener('blur', () => {
      routeSkipLink.remove();
    });
    routeSkipLink.addEventListener('click', (e) => {
      // Prevent default so 'blur' event doesn't get triggered on click
      e.preventDefault();

      handleSkipLink(headerId);
    });
    routeSkipLink.addEventListener('keydown', (e: KeyboardEvent) => {
      e.preventDefault();

      if (e.key === 'Tab') {
        header.focus();
      } else if (e.key === 'Enter') {
        handleSkipLink(headerId);
      }
    });

    // Insert skip link into header and focus it
    header.appendChild(routeSkipLink);
    routeSkipLink.focus();
  };

  useEffect(() => {
    // Don't engage route skip link if modal is present
    if (queryParams.modal) {
      return;
    }

    if (location.hash) {
      renderSkipLinks(location.hash);
    }
  }, [location, ...renderOnChange]);
  return renderSkipLinks;
};
